import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BusinessAutomationTriggerTypeEnum } from '@reach/shared-types/business-automation';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { StepDialogFooter } from '../StepDialogFooter';
import styles from './SettingsControls.module.css';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

type CooldownUnit = 'minutes' | 'hours' | 'days';
const unitToMultiplier: Record<CooldownUnit, number> = {
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
};

function secondsFromCooldown(value: number, unit: CooldownUnit): number {
  return value * unitToMultiplier[unit];
}

function cooldownFromSeconds(seconds: number): {
  value: number;
  unit: CooldownUnit;
} {
  let result: ReturnType<typeof cooldownFromSeconds> = {
    value: 0,
    unit: 'minutes',
  };
  const units: CooldownUnit[] = ['days', 'hours', 'minutes'];
  for (const unit of units) {
    if (seconds >= unitToMultiplier[unit]) {
      result = { value: Math.ceil(seconds / unitToMultiplier[unit]), unit };
      break;
    }
  }
  return result;
}

function formatCooldownSeconds(seconds: number): string {
  const { value, unit } = cooldownFromSeconds(seconds);
  const singular = unit.slice(0, -1);
  return `${value} ${value === 1 ? singular : unit}`;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, updateSettings, template } = editor;
  const { localContents } = state;
  const triggerMetadata = localContents.triggerMetadata;

  const isTriggerBased =
    triggerMetadata?.triggerType ===
    BusinessAutomationTriggerTypeEnum.TRIGGER_BASED;
  const cooldownSeconds = isTriggerBased ? triggerMetadata.cooldownSeconds : 0;

  const originalTriggerMetadata = template.contents.triggerMetadata;
  const originalCooldownSeconds =
    originalTriggerMetadata?.triggerType ===
    BusinessAutomationTriggerTypeEnum.TRIGGER_BASED
      ? originalTriggerMetadata.cooldownSeconds
      : 0;

  const { value, unit } = cooldownFromSeconds(cooldownSeconds);

  const [name, setName] = useState(localContents.name);
  const [description, setDescription] = useState(localContents.description);
  const [cooldownValue, setCooldownValue] = useState(value);
  const [cooldownUnit, setCooldownUnit] = useState(unit);

  useEffect(() => {
    setName(localContents.name);
    setDescription(localContents.description);
    const { value: newValue, unit: newUnit } =
      cooldownFromSeconds(cooldownSeconds);
    setCooldownValue(newValue);
    setCooldownUnit(newUnit);
  }, [localContents.name, localContents.description, cooldownSeconds]);

  if (!open) return null;

  const handleOK = () => {
    updateSettings({
      name,
      description,
      cooldownSeconds: secondsFromCooldown(cooldownValue, cooldownUnit),
    });
    onClose();
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>Automation Settings</h2>
            <p>Configure settings for this automation</p>
          </hgroup>
          <button onClick={onClose} className={styles.closeButton}>
            <X />
          </button>
        </header>

        <main>
          <fieldset>
            <legend>Name</legend>
            <input
              type="text"
              value={name}
              onChange={({ target: { value } }) => setName(value)}
              placeholder="Automation name"
            />
          </fieldset>

          <fieldset>
            <legend>Description</legend>
            <textarea
              value={description}
              onChange={({ target: { value } }) => setDescription(value)}
              placeholder="Describe what this automation does"
              rows={3}
            />
          </fieldset>

          {isTriggerBased && (
            <fieldset>
              <legend>Cooldown Period</legend>
              <p>
                Prevent customers from entering this automation more than once
                within the specified time period.
              </p>
              <div className={styles.cooldownRow}>
                <input
                  type="number"
                  min="0"
                  value={cooldownValue}
                  onChange={({ target: { value } }) =>
                    setCooldownValue(Number(value))
                  }
                />
                <select
                  value={cooldownUnit}
                  onChange={({ target: { value } }) =>
                    setCooldownUnit(value as CooldownUnit)
                  }>
                  <option value="hours">Hours</option>
                  <option value="minutes">Minutes</option>
                  <option value="days">Days</option>
                </select>
              </div>
              <small>
                Current cooldown:{' '}
                {formatCooldownSeconds(originalCooldownSeconds)}
              </small>
            </fieldset>
          )}
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleOK} />
      </dialog>
    </>
  );
};
